import apiClient from './client';

export type ConversationType = 'dialogue' | 'support';
export type ConversationStatus = 'open' | 'pending' | 'resolved' | 'closed';
export type ConversationSenderRole = 'user' | 'assistant' | 'agent' | 'system';
export type ConversationPriority = 'low' | 'normal' | 'high' | 'urgent';
export type ConversationVisibility = 'public' | 'internal';
export type ConversationInboxView = 'mine' | 'unassigned' | 'awaiting' | 'all' | 'resolved';
export type ConversationScopeMode = 'assigned' | 'queue' | 'project' | 'company';
export type AgentPresenceState = 'online' | 'away' | 'offline';

export interface ConversationThread {
  conversationId: string;
  tenantKey: string;
  companyId: string;
  projectId: string;
  ownerSubjectId: string;
  conversationType: ConversationType;
  status: ConversationStatus;
  title: string | null;
  assignedAgentId: string | null;
  sourceRevision: number;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  externalReference: string | null;
  // 客服工作台欄位 / service-desk fields
  queueId: string | null;
  assignedAt: string | null;
  assignedBy: string | null;
  lastMessageAt: string | null;
  lastMessageRole: ConversationSenderRole | null;
  lastMessagePreview: string | null;
  messageCount: number;
  awaitingReply: boolean;
  waitingSince: string | null;
  firstResponseAt: string | null;
  firstResponseSeconds: number;
  priority: ConversationPriority;
  tags: string[] | null;
  lockedBy: string | null;
  lockUntil: string | null;
  unreadHintRevision: number;
  lastReadRevision: number;
  hasUnread: boolean;
  effectiveQueueId: string;
}

export interface ConversationMessage {
  messageId: string;
  conversationId: string;
  tenantKey: string;
  projectId: string;
  conversationType: ConversationType;
  senderRole: ConversationSenderRole;
  senderSubjectId: string;
  content: string;
  replyToMessageId: string | null;
  sourceRevision: number;
  createdAt: string;
  providerRevision: string | null;
  visibility: ConversationVisibility;
}

export interface ConversationPage<T> {
  items: T[];
  nextSearchAfter: string | null;
}

export interface ConversationWrite<T> {
  resource: T;
  created: boolean;
  projectionQueued: boolean;
  warningCode: string | null;
}

export interface ConversationDeskCapabilities {
  companyId: string;
  agentSubjectId: string;
  canRead: boolean;
  canReplyOrResolve: boolean;
  canApproveReuse: boolean;
  canAssign: boolean;
  canNote: boolean;
  isAdmin: boolean;
  scopeMode: ConversationScopeMode;
  allowedProjectIds: string[];
  allowedQueueIds: string[];
}

export interface ConversationProjectAccess {
  projectId: string;
  displayName: string;
  isDefault: boolean;
}

export interface ConversationInboxCounters {
  mine: number;
  unassigned: number;
  awaiting: number;
  all: number;
  unreadTotal: number;
}

export interface ConversationChangesResult {
  changed: ConversationThread[];
  serverTime: string;
  truncated: boolean;
}

export interface ConversationAssignmentEvent {
  conversationId: string;
  eventType: string;
  eventActorId: string;
  eventFrom: string | null;
  eventTo: string | null;
  eventNote: string | null;
  sourceRevision: number;
  createdAt: string;
}

export interface ConversationAgentSummary {
  agentSubjectId: string;
  presenceState: AgentPresenceState;
  heartbeatAt: string | null;
  activeConversationCount: number;
}

export interface ConversationQueue {
  queueId: string;
  displayName: string;
  projectIds: string[];
  enabled: boolean;
}

export interface ConversationLockResult {
  acquired: boolean;
  lockedBy: string | null;
  lockUntil: string | null;
}

export interface ConversationInboxFilters {
  view?: ConversationInboxView;
  projectId?: string;
  queueId?: string;
  conversationType?: ConversationType;
  search?: string;
  searchAfter?: string;
  limit?: number;
}

const basePath = '/conversations';
const deskPath = `${basePath}/desk`;

function idempotencyKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function threadPath(conversationId: string, suffix: string): string {
  return `${deskPath}/threads/${encodeURIComponent(conversationId)}/${suffix}`;
}

export async function getDeskCapabilities() {
  const response = await apiClient.get<ConversationDeskCapabilities>(`${deskPath}/capabilities`);
  return response.data;
}

export async function getConversationProjects() {
  const response = await apiClient.get<ConversationProjectAccess[]>(`${basePath}/projects`);
  return response.data;
}

export async function getDeskQueues() {
  const response = await apiClient.get<ConversationQueue[]>(`${deskPath}/queues`);
  return response.data;
}

export async function getDeskAgents() {
  const response = await apiClient.get<ConversationAgentSummary[]>(`${deskPath}/agents`);
  return response.data;
}

export async function getInbox(filters: ConversationInboxFilters) {
  const response = await apiClient.get<ConversationPage<ConversationThread>>(`${deskPath}/inbox`, {
    params: filters,
  });
  return response.data;
}

/**
 * 中文：增量輪詢的主力端點。只回自 since 之後有變動的 thread 摘要，不含訊息全文。
 * English: The polling workhorse. Returns only thread summaries changed since the cursor, never message bodies.
 */
export async function getInboxChanges(
  since: string | null,
  projectId?: string,
  queueId?: string,
  limit = 100
) {
  const response = await apiClient.get<ConversationChangesResult>(`${deskPath}/inbox/changes`, {
    params: { since: since ?? undefined, projectId, queueId, limit },
  });
  return response.data;
}

export async function getInboxCounters() {
  const response = await apiClient.get<ConversationInboxCounters>(`${deskPath}/inbox/counters`);
  return response.data;
}

/**
 * 中文：增量取訊息。afterRevision 為目前已載入的最大 sourceRevision，0 代表首次載入。
 * English: Incremental message fetch. afterRevision is the highest sourceRevision already loaded; 0 means first load.
 */
export async function getMessagesAfter(
  conversationId: string,
  afterRevision: number,
  limit = 100
) {
  const response = await apiClient.get<ConversationPage<ConversationMessage>>(
    threadPath(conversationId, 'messages'),
    { params: { afterRevision, limit } }
  );
  return response.data;
}

export async function getConversationEvents(conversationId: string) {
  const response = await apiClient.get<ConversationAssignmentEvent[]>(
    threadPath(conversationId, 'events')
  );
  return response.data;
}

export async function claimConversation(conversationId: string) {
  const response = await apiClient.post<ConversationThread>(threadPath(conversationId, 'claim'), {});
  return response.data;
}

export async function assignConversation(
  conversationId: string,
  agentSubjectId: string | null,
  note?: string
) {
  const response = await apiClient.post<ConversationThread>(threadPath(conversationId, 'assign'), {
    agentSubjectId,
    note: note ?? null,
  });
  return response.data;
}

export async function transferConversation(
  conversationId: string,
  toAgentSubjectId: string | null,
  toQueueId: string | null,
  note?: string
) {
  const response = await apiClient.post<ConversationThread>(threadPath(conversationId, 'transfer'), {
    toAgentSubjectId,
    toQueueId,
    note: note ?? null,
  });
  return response.data;
}

export async function changeConversationStatus(
  conversationId: string,
  status: ConversationStatus,
  note?: string
) {
  const response = await apiClient.post<ConversationThread>(threadPath(conversationId, 'status'), {
    status,
    note: note ?? null,
  });
  return response.data;
}

export async function setConversationPriority(
  conversationId: string,
  priority: ConversationPriority
) {
  const response = await apiClient.post<ConversationThread>(threadPath(conversationId, 'priority'), {
    priority,
  });
  return response.data;
}

export async function setConversationTags(conversationId: string, tags: string[]) {
  const response = await apiClient.post<ConversationThread>(threadPath(conversationId, 'tags'), {
    tags,
  });
  return response.data;
}

export async function markConversationRead(conversationId: string, revision: number) {
  await apiClient.post(threadPath(conversationId, 'read'), { revision });
}

export async function acquireConversationLock(conversationId: string, force = false) {
  try {
    const response = await apiClient.post<ConversationLockResult>(
      threadPath(conversationId, 'lock'),
      { force }
    );
    return response.data;
  } catch (error) {
    // 中文：409 代表被他人持有，回應本文仍含持鎖者，屬預期結果而非例外。
    // English: A 409 means someone else holds it; the body still carries the holder, so this is expected, not an error.
    const conflict = (error as { response?: { status?: number; data?: ConversationLockResult } })
      .response;
    if (conflict?.status === 409 && conflict.data) return conflict.data;
    throw error;
  }
}

export async function releaseConversationLock(conversationId: string) {
  await apiClient.delete(threadPath(conversationId, 'lock'));
}

export async function updateAgentPresence(state: AgentPresenceState) {
  const response = await apiClient.post<ConversationAgentSummary>(`${deskPath}/agents/me/presence`, {
    state,
  });
  return response.data;
}

export async function replyToConversation(conversationId: string, content: string) {
  const response = await apiClient.post<ConversationWrite<ConversationMessage>>(
    `${basePath}/threads/${encodeURIComponent(conversationId)}/messages`,
    {
      senderRole: 'agent',
      content,
      replyToMessageId: null,
      providerRevision: null,
    },
    { headers: { 'Idempotency-Key': idempotencyKey('crm-agent') } }
  );
  return response.data;
}

export async function appendInternalNote(conversationId: string, content: string) {
  const response = await apiClient.post<ConversationWrite<ConversationMessage>>(
    threadPath(conversationId, 'notes'),
    { content },
    { headers: { 'Idempotency-Key': idempotencyKey('crm-note') } }
  );
  return response.data;
}

export async function resolveConversation(
  conversationId: string,
  resolutionNote: string,
  approveAnswerForReuse: boolean,
  validUntil: string | null
) {
  const response = await apiClient.post<ConversationThread>(
    `${basePath}/admin/threads/${encodeURIComponent(conversationId)}/resolve`,
    { resolutionNote, approveAnswerForReuse, validUntil }
  );
  return response.data;
}
